<script setup lang="ts">
// «Как работает Sobes» as one scroll: a pinned phone whose screen follows the
// steps. On phones the step text is pinned under it and the articles only pace
// the scroll; on desktop the articles scroll beside the phone.
import type { FeedVacancyItem } from '~/entities/feed'
import { steps, type LandingAudience } from './landing-content'
import LandingLiveFeed from './LandingLiveFeed.vue'
import LandingMockCandidate from './LandingMockCandidate.vue'
import LandingMockPush from './LandingMockPush.vue'
import LandingMockRecorder from './LandingMockRecorder.vue'
import LandingMockResponses from './LandingMockResponses.vue'
import LandingMockVacancyBuilder from './LandingMockVacancyBuilder.vue'
import LandingPhone from './LandingPhone.vue'

defineProps<{ feed: FeedVacancyItem[] }>()

type Screen = 'recorder' | 'feed' | 'push' | 'builder' | 'responses' | 'candidate'

const chapters: { audience: LandingAudience, title: string, accent: string, dot: string, screens: Screen[] }[] = [
  { audience: 'applicant', title: 'Соискателю', accent: 'text-primary', dot: 'bg-primary', screens: ['recorder', 'feed', 'push'] },
  { audience: 'employer', title: 'Работодателю', accent: 'text-orange-600', dot: 'bg-orange-500', screens: ['builder', 'responses', 'candidate'] },
]
const items = chapters.flatMap(chapter => steps[chapter.audience].map((step, index) => ({
  ...step,
  chapter,
  index,
  screen: chapter.screens[index]!,
})))

const active = ref(0)
const current = computed(() => items[active.value]!)
const articles = useTemplateRef<HTMLElement[]>('articles')
let observer: IntersectionObserver | null = null

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) active.value = Number((entry.target as HTMLElement).dataset.index)
    }
  }, { rootMargin: '-50% 0px -50% 0px' })
  articles.value?.forEach(article => observer!.observe(article))
})
onBeforeUnmount(() => observer?.disconnect())
</script>

<template>
  <div class="relative mx-auto max-w-6xl lg:grid lg:grid-cols-[1fr_auto] lg:gap-20 lg:px-6">
    <div class="sticky top-14 flex h-[calc(100svh-3.5rem)] flex-col items-center gap-4 px-4 pb-24 pt-4 lg:order-2 lg:top-0 lg:h-svh lg:justify-center lg:self-start lg:p-0">
      <div class="flex min-h-0 w-full flex-1 justify-center lg:flex-none">
        <div class="pointer-events-none relative aspect-[360/760] h-full max-w-full lg:h-auto lg:w-72">
          <LandingPhone>
            <Transition mode="out-in" enter-from-class="opacity-0 scale-95" leave-to-class="opacity-0 scale-105" enter-active-class="transition duration-300" leave-active-class="transition duration-200">
              <LandingMockRecorder v-if="current.screen === 'recorder'" key="recorder" :clip="2" />
              <LandingLiveFeed v-else-if="current.screen === 'feed'" key="feed" :items="feed" />
              <LandingMockPush v-else-if="current.screen === 'push'" key="push" />
              <LandingMockVacancyBuilder v-else-if="current.screen === 'builder'" key="builder" />
              <LandingMockResponses v-else-if="current.screen === 'responses'" key="responses" />
              <LandingMockCandidate v-else key="candidate" />
            </Transition>
          </LandingPhone>
          <ol class="absolute -right-5 top-1/2 hidden -translate-y-1/2 flex-col gap-1.5 lg:-right-10 lg:flex" aria-hidden="true">
            <li
              v-for="(item, index) in items"
              :key="index"
              class="w-1.5 rounded-full transition-all duration-300"
              :class="[index === active ? 'h-6' : 'h-1.5 opacity-30', item.chapter.dot]"
            />
          </ol>
        </div>
      </div>

      <div class="relative h-36 w-full lg:hidden" aria-hidden="true">
        <Transition enter-from-class="translate-y-3 opacity-0" leave-to-class="-translate-y-3 opacity-0" enter-active-class="transition duration-300" leave-active-class="transition duration-200 absolute inset-x-0 top-0">
          <div :key="active" class="w-full">
            <div class="flex items-center gap-3">
              <p class="text-sm font-semibold uppercase tracking-[0.14em]" :class="current.chapter.accent">{{ current.chapter.title }}</p>
              <span class="flex gap-1">
                <span
                  v-for="(item, index) in items"
                  :key="index"
                  class="h-1.5 rounded-full transition-all duration-300"
                  :class="[index === active ? 'w-5' : 'w-1.5 opacity-30', item.chapter.dot]"
                />
              </span>
            </div>
            <p class="mt-1.5 text-2xl font-bold leading-tight">{{ current.title }}</p>
            <p class="mt-1 text-[15px] leading-snug text-muted-foreground">{{ current.text }}</p>
          </div>
        </Transition>
      </div>
    </div>

    <div class="relative -mt-[calc(100svh-3.5rem)] lg:order-1 lg:mt-0">
      <article
        v-for="(item, index) in items"
        ref="articles"
        :key="`${item.chapter.audience}-${item.index}`"
        :data-index="index"
        class="h-[60svh] lg:flex lg:h-auto lg:min-h-svh lg:items-center"
      >
        <div class="sr-only transition-opacity duration-300 lg:not-sr-only" :class="index === active ? '' : 'lg:opacity-30'">
          <p class="text-sm font-semibold uppercase tracking-[0.14em]" :class="item.chapter.accent">
            {{ item.chapter.title }} · {{ item.index + 1 }}/3
          </p>
          <h3 class="mt-2 text-5xl font-bold leading-tight">{{ item.title }}</h3>
          <p class="mt-4 max-w-md text-xl text-muted-foreground">{{ item.text }}</p>
        </div>
      </article>
    </div>
  </div>
</template>
